// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  // Email-not-verified state — keeps the offer-to-resend banner visible
  // even after the user changes the password field.
  const [unverified, setUnverified] = useState(null);   // { email } | null
  const [resending, setResending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setUnverified(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      toast.success("Welcome back");
      nav("/");
    } catch (e2) {
      const detail = e2.response?.data?.detail;
      // Backend returns {code:'email_not_verified', message, email} on 403.
      if (detail && typeof detail === "object" && detail.code === "email_not_verified") {
        setUnverified({ email: detail.email || email });
        setErr(detail.message || "Please confirm your email to continue.");
      } else {
        setErr(formatApiError(detail) || "Login failed");
      }
    } finally { setBusy(false); }
  };

  const resendVerification = async () => {
    if (!unverified?.email || resending) return;
    setResending(true);
    try {
      const { data } = await api.post("/auth/resend-verification", { email: unverified.email });
      if (data?.rate_limited) {
        toast("We just sent one — check spam, or try again in 60 seconds.", { duration: 4500 });
      } else {
        toast.success("Confirmation email sent. Check your inbox.");
      }
    } catch (_e) {
      toast.error("We couldn't resend right now. Try again in a minute.");
    } finally {
      setResending(false);
    }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10 relative">
      <img src="https://customer-assets.emergentagent.com/job_live-broadcast-53/artifacts/xnmramec_0f112e07-9ce5-403f-a155-6e62b27aadfc.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/75 to-black" />

      <div className="relative tv-glass rounded-2xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/tapintv-logo-v2.png" alt="Tap IN TV" className="h-56 w-auto drop-shadow-[0_0_50px_rgba(250,204,21,0.45)]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold mt-4">Tap IN TV Network</div>
          <div className="font-display font-black text-3xl text-white mt-1">Welcome back</div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Email</label>
            <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-white/30 focus:border-tv-brand outline-none" placeholder="you@email.com" />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Password</label>
            <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white focus:border-tv-brand outline-none" />
          </div>
          {err && <div className="text-sm text-red-400" data-testid="login-error">{err}</div>}
          {unverified && (
            <div
              data-testid="login-unverified-banner"
              className="bg-amber-500/15 border border-amber-400/40 rounded-xl px-4 py-3 text-sm text-amber-100"
            >
              <div className="font-bold text-amber-200 text-xs uppercase tracking-wider mb-1">Confirm your email</div>
              <div>We sent a confirmation link to <b>{unverified.email}</b>. Click it to unlock your account.</div>
              <button
                type="button"
                data-testid="login-resend-verification-btn"
                onClick={resendVerification}
                disabled={resending}
                className="mt-2 inline-flex items-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black font-bold text-xs px-3 py-1.5 rounded-full"
              >
                {resending ? "Resending…" : "Resend confirmation email"}
              </button>
            </div>
          )}
          <button type="submit" data-testid="login-submit-btn" id="login-submit" disabled={busy} className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-bold py-3 rounded-full transition disabled:opacity-60">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="relative my-5 text-center">
          <span className="bg-transparent text-white/40 text-xs uppercase tracking-wider">or</span>
        </div>

        <button data-testid="login-google-btn" onClick={google} className="w-full border border-white/20 hover:bg-white/5 text-white font-semibold py-3 rounded-full flex items-center justify-center gap-3">
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1h-9.17v2.8h5.28c-.23 1.4-1.7 4.1-5.28 4.1-3.18 0-5.78-2.63-5.78-5.87S8.99 6.26 12.18 6.26c1.81 0 3.02.77 3.72 1.43l2.54-2.44C16.8 3.6 14.74 2.75 12.18 2.75 6.98 2.75 2.8 6.93 2.8 12.13s4.18 9.38 9.38 9.38c5.41 0 8.99-3.8 8.99-9.16 0-.62-.07-1.08-.16-1.25z"/></svg>
          Continue with Google
        </button>

        <div className="mt-6 text-sm text-white/70 text-center">
          New to Tap In TV? <Link to="/signup" className="text-tv-brand hover:underline font-semibold">Create an account</Link>
        </div>

        <div className="mt-4 text-xs text-white/40 text-center">
          Demo admin: admin@tapintv.com / TapIn@2026
        </div>
      </div>
    </div>
  );
}
